import React from "react";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useAdminMetrics, useIsAdmin } from "@/hooks/useAdminMetrics";
import { AdminMetricsGrid } from "@/components/admin/AdminMetricsGrid";
import { AdminTimelineChart } from "@/components/admin/AdminTimelineChart";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Download, RefreshCw, ShieldAlert } from "lucide-react";
import { toast } from "@/hooks/use-toast";

export default function AdminMetrics() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { data: isAdmin, isLoading: adminLoading } = useIsAdmin();
  const [daysBack, setDaysBack] = useState(30);

  const {
    userMetrics,
    projectMetrics,
    aiMetrics,
    deployMetrics,
    collaborationMetrics,
    timeline,
    isLoading,
    refetchAll,
  } = useAdminMetrics(daysBack);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate("/auth?redirect=/admin/metrics");
    }
  }, [authLoading, user, navigate]);

  useEffect(() => {
    if (!adminLoading && isAdmin === false && user) {
      navigate("/");
      toast({
        title: "Access Denied",
        description: "You don't have permission to access this page.",
        variant: "destructive",
      });
    }
  }, [adminLoading, isAdmin, user, navigate]);

  const handleExportCSV = () => {
    if (!userMetrics || !projectMetrics || !aiMetrics || !deployMetrics || !collaborationMetrics) {
      return;
    }

    const rows = [
      ["Metric Category", "Metric Name", "Value"],
      // User Metrics
      ["Users", "Total Users", userMetrics.total_users],
      ["Users", "DAU", userMetrics.daily_active_users],
      ["Users", "WAU", userMetrics.weekly_active_users],
      ["Users", "MAU", userMetrics.monthly_active_users],
      ["Users", "Onboarding %", `${userMetrics.onboarding_completion_pct}%`],
      ["Users", "7-Day Retention", `${userMetrics.retention_7d_pct}%`],
      ["Users", "30-Day Retention", `${userMetrics.retention_30d_pct}%`],
      // Project Metrics
      ["Projects", "Total Projects", projectMetrics.total_projects],
      ["Projects", "Projects Today", projectMetrics.projects_today],
      ["Projects", "Avg Files/Project", projectMetrics.avg_files_per_project],
      ["Projects", "Avg AI Actions/Project", projectMetrics.avg_ai_actions_per_project],
      // AI Metrics
      ["AI Usage", "Total Requests", aiMetrics.total_requests],
      ["AI Usage", "Avg Requests/User", aiMetrics.avg_requests_per_user],
      ["AI Usage", "Scaffolds", aiMetrics.scaffold_count],
      ["AI Usage", "Code Agent", aiMetrics.code_agent_count],
      ["AI Usage", "Success Rate", `${aiMetrics.success_rate}%`],
      // Deploy Metrics
      ["Deploys", "Total Deploys", deployMetrics.total_deploys],
      ["Deploys", "Success Rate", `${deployMetrics.success_rate}%`],
      ["Deploys", "Unique Deployers", deployMetrics.unique_deployers],
      // Collaboration Metrics
      ["Collaboration", "Collaborative Projects", collaborationMetrics.projects_with_collaborators],
      ["Collaboration", "Avg Collaborators", collaborationMetrics.avg_collaborators_per_project],
      ["Collaboration", "Total Messages", collaborationMetrics.total_chat_messages],
    ];

    if (timeline && timeline.length > 0) {
      rows.push([""]);
      rows.push(["Date", "New Users", "New Projects", "AI Requests", "Deploys", "Messages"]);
      timeline.forEach((t) => {
        rows.push([t.date, t.new_users, t.new_projects, t.ai_requests, t.deploys, t.chat_messages]);
      });
    }

    const csvContent = rows.map((row) => row.join(",")).join("\n");
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `admin-metrics-${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);

    toast({ title: "CSV exported successfully" });
  };

  if (authLoading || adminLoading) {
    return (
      <div className="container mx-auto p-6 max-w-7xl">
        <div className="space-y-6">
          <Skeleton className="h-10 w-64" />
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {[...Array(8)].map((_, i) => (
              <Skeleton key={i} className="h-32" />
            ))}
          </div>
          <Skeleton className="h-80" />
        </div>
      </div>
    );
  }

  if (!user || isAdmin === false) {
    return (
      <div className="container mx-auto p-6 max-w-7xl">
        <Card>
          <CardContent className="py-12 text-center">
            <ShieldAlert className="mx-auto h-12 w-12 text-destructive mb-4" />
            <h3 className="text-lg font-semibold mb-2">Access Denied</h3>
            <p className="text-muted-foreground">
              You don't have permission to access this page.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 max-w-7xl">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold mb-2">Admin Analytics</h1>
          <p className="text-muted-foreground">
            SaaS metrics and platform insights
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Tabs
            value={String(daysBack)}
            onValueChange={(v) => setDaysBack(Number(v))}
          >
            <TabsList>
              <TabsTrigger value="7">7 days</TabsTrigger>
              <TabsTrigger value="30">30 days</TabsTrigger>
              <TabsTrigger value="90">90 days</TabsTrigger>
            </TabsList>
          </Tabs>
          <Button variant="outline" size="icon" onClick={refetchAll} disabled={isLoading}>
            <RefreshCw className={`h-4 w-4 ${isLoading ? "animate-spin" : ""}`} />
          </Button>
          <Button variant="outline" onClick={handleExportCSV}>
            <Download className="mr-2 h-4 w-4" />
            Export CSV
          </Button>
        </div>
      </div>

      {isLoading ? (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {[...Array(8)].map((_, i) => (
              <Skeleton key={i} className="h-32" />
            ))}
          </div>
          <Skeleton className="h-80" />
        </div>
      ) : (
        <div className="space-y-8">
          {timeline && timeline.length > 0 && (
            <AdminTimelineChart data={timeline} />
          )}

          <AdminMetricsGrid
            userMetrics={userMetrics ?? null}
            projectMetrics={projectMetrics ?? null}
            aiMetrics={aiMetrics ?? null}
            deployMetrics={deployMetrics ?? null}
            collaborationMetrics={collaborationMetrics ?? null}
          />
        </div>
      )}
    </div>
  );
}
